import React from 'react'
import { useCallback, useEffect } from 'react'
import InputField from '../form/InputField'
import FormWrapper from '../form/FormWrapper'
import { memo } from 'react'
import CategoryImage from '../CategoryImage'
import { uniformUrl } from '../../helpers/StringHelpers'
import { ProductTypeHelper } from '../../helpers/ProductTypeHelper'
import useFormState from '../form/hooks/useFormState'
import PropTypes from 'prop-types'
import isEmail from '../form/validators/isEmail'
import { useDispatch } from 'react-redux'
import { hideChat, unhideChat } from '../../state/popupForms'
import isPhone from '../form/validators/isPhone'


import algoliasearch from 'algoliasearch/lite'
const appId = process.env.GATSBY_ALGOLIA_APP
const apiKey = process.env.GATSBY_ALGOLIA_KEY

const IconHeartSvg = ({ className }) => {
  return (
    <svg
      className={'icon icon-heart fill-current ' + className}
      version="1.1"
      viewBox="-2 -2 36 36"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="https://www.w3.org/1999/xlink"
      aria-label="Favorite"
    >
      <path d="M23.6 2c-3.363 0-6.258 2.736-7.599 5.594-1.342-2.858-4.237-5.594-7.601-5.594-4.637 0-8.4 3.764-8.4 8.401 0 9.433 9.516 11.906 16.001 21.232 6.13-9.268 15.999-12.1 15.999-21.232 0-4.637-3.763-8.401-8.4-8.401z"></path>
    </svg>
  )
}
IconHeartSvg.propTypes = {
  className: PropTypes.string,
}
export const IconHeart = memo(IconHeartSvg)

const FormatKeyValue = (key, value, item) => {
  switch(key) {
    case 'case_shape': return `${value} Case`
    case 'case_back': return `${value} Case`
    case 'band_color': return `${value} Band`
    case 'dial': return `${value} Dial`
    case 'dial_style': return `${value} Dial`
    case 'model_no': return `Ref# ${value}`
    case 'case_material': return `${value} Case`
    case 'gender_mfu': return `${value} ${item.item_category}`
    default: return value
  }
}

const WhatDoYouLikeFeature = ({ allowed, product, toggleKey, form, similarItems }) => {
  return (
    <div>
      What do you like about this product?
      <div className="flex gap-2 flex-wrap my-2">
        {allowed.map((key, index) => {
          let value = product[key]
          if (value?.strapi_json_value) {
            value = value.strapi_json_value
          }
          if (!Array.isArray(value)) {
            value = [value]
          }
          return value
            .filter(f => f)
            .map(val => (
              <button
                onClick={e => toggleKey(e, encodeURIComponent(key) + '=' + encodeURIComponent(val))}
                key={key + ':' + val}
                className={
                  'text-gray-800 text-sm px-2 rounded-full text-center ' +
                  (form.features.indexOf(encodeURIComponent(key) + '=' + encodeURIComponent(val)) >=
                  0
                    ? 'bg-green-300'
                    : 'bg-gray-100')
                }
              >
                {FormatKeyValue(key, val, product)}
              </button>
            ))
        })}

        {form.features.length === 0 && (
          <span className="text-red-500 text-sm">Select features you like</span>
        )}
        {similarItems?.hits?.length > 0 && (
          <span className="text-gray-500 text-sm">
            There are {similarItems?.hits?.length} more items that match your selection.
            We will notify you with similar items.
          </span>
        )}
      </div>
    </div>
  )
}

const FavoriteItems = ({ product, setFavorite }) => {
  const dispatch = useDispatch()
  const url = uniformUrl(product.url + '/')
  const onClosePopup = useCallback(e => {
    if (e && e.preventDefault) e.preventDefault()
    if (setFavorite) setFavorite(null)
    dispatch(unhideChat())
  }, [])

  const handleKeyPress = e => {
    switch (e.key) {
      case 'Escape':
        if (setFavorite) setFavorite(null)
        break
      default:
        break
    }
  }
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    window.addEventListener('keydown', handleKeyPress)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [setFavorite])
  const [similarItems, setSimilarItems] = React.useState({})
  const [form, setForm, updateForm] = useFormState({
    name: '',
    email: '',
    phone: '',
    sku: '',
    product: '',
    consent: true,
    features: [],
    step: 0,
  })
  const allowed = [
    'gender_mfu',
    'brand',
    'model',
    'model_no',
    'nickname',
    'item_category',
    'box_and_papers',
    'main_stone',
    'metal_name',
    'features',
    'dial_style',
    'dial',
    'case_shape',
    'case_back',
    'band_type',
    'band_color',
    'case_material',
  ]

  const gotoStep2 = useCallback(e => {
    e.preventDefault()
    setForm(f => ({ ...f, step: 1 }))
    dispatch(hideChat())
  }, [setForm, dispatch])

  useEffect(() => {
    const searchClient = algoliasearch(appId, apiKey)
    const index = searchClient.initIndex('products')
    index.search('', {
      hitsPerPage: 36,
      responseFields: '*',
      attributesToRetrieve: [
        'media_gallery',
        'name',
        'categories',
        'urlPath',
        'sku',
        'title',
        'small_image',
        'thumbnail',
        'item_type',
        'item_category',
        'jewelry_type',
        'brand',
        'model',
        'model_no',
        'nickname',
        'case_size_mm',
        'price',
        'special_price',
        'qty',
        'original_box',
        'original_papers',
        'gender_mfu',
        'box_and_papers',
        'main_stone',
        'metal_name',
        'features',
        'dial_style',
        'dial',
        'case_shape',
        'case_back',
        'band_type',
        'band_color',
        'case_material',
      ],
      attributesToHighlight: false,
      attributesToSnippet: false,
      // attributesToSnippet: '*:20',
      // snippetEllipsisText: '…',
      facetFilters: [['in_stock:true'], ['sku:-' + product.sku], ...form.features.map(f => [f.replace(/=/g, ':').replace(/%20/g, ' ')])],
      // optionalFilters: ,
      facets: ['*'],
    }).then((resp) => {
      setSimilarItems(resp)
    })
  }, [form.features])

  useEffect(() => {
    if (product) {
      setForm(f => ({ ...f, product: product.name, sku: product.sku, features: [`brand=${encodeURIComponent(product.brand)}`, `item_category=${encodeURIComponent(product.item_category)}`]  }))
      dispatch(hideChat())
    }
  }, [product])
  const onChangeHandler = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.checked }))
  }
  const toggleKey = (e,key) => {
    e.preventDefault()
    setForm(f => {
      const features = f.features.includes(key)
        ? f.features.filter(k => k !== key)
        : [...f.features, key]
      return { ...f, features }
    })
  }
  return (
    <div className="fade-in-now fixed top-0 left-0 flex h-full w-full items-center justify-center lg:h-screen z-popup bg-white md:bg-transparent backdrop-blur-sm">
      <button
        className="inset-0 cursor-alias absolute bg-slate-400 opacity-40"
        onClick={onClosePopup}
      />

      <div className="fade-in-favorite absolute top-[50%] left-[50%] flex w-full -translate-y-[50%] -translate-x-[50%] flex-col md:flex-row gap-3 rounded-xl bg-white p-5 md:shadow-md shorter768:h-screen shorter768:w-screen md:w-max justify-center items-center ">
        <div className="aspect-square object-contain flex justify-center items-center max-w-[300px]">
          <CategoryImage
            sku={product.sku}
            alt={product.name}
            title={product.title}
            normal={product.small_image}
            url={url}
            productType={product.productType || ProductTypeHelper(product)}
            tags={false}
            imgStyle={{ objectFit: 'contain' }}
            product={product}
            className={'w-full h-full aspect-square'}
          />
        </div>
        <div className="col-span-2 flex flex-col justify-center  sm:col-span-1 max-w-[400px] ">
          <div className="mx-auto flex max-w-max flex-row flex-wrap items-center justify-center  pr-4 text-center text-4xl font-black text-red-700 sm:text-3xl">
            <IconHeart className=" relative mx-3 h-10 w-10 animate-pulse " />
            NOTIFY ME
          </div>
          <p className="mx-auto my-2 w-10/12 text-center text-gray-700">
            Enter your information and we will notify you with similar items or price changes.
          </p>
          <FormWrapper
            form={form}
            name={'Favorite Item Alerts'}
            action={gotoStep2}
            method={'POST'}
            className="flex flex-col"
          >
            {form.step === 0 ? (
            <>
            <InputField
              errorMessage=""
              name={'name'}
              label={'Name'}
              form={form}
              onChange={updateForm}
              required={true}
            />
            <InputField
              errorMessage=""
              name={'email'}
              label={'E-Mail'}
              form={form}
              validator={isEmail}
              onChange={updateForm}
              className={'w-full'}
              required={true}
            />
            <InputField
              errorMessage=""
              name={'phone'}
              label={'Phone'}
              form={form}
              validator={isPhone}
              onChange={updateForm}
              className={'w-full'}
              required={true}
            />
            <label className="text-xs text-gray-500 flex flex-row gap-3 items-start my-2">
              <input
                type="checkbox"
                name="consent"
                checked={form['consent']}
                onChange={onChangeHandler}
              />
              I agree to receive updates and follow-up messages about my inquiries via text and
              email.
            </label>
            </>) : (
              <WhatDoYouLikeFeature
                allowed={allowed}
                product={product}
                toggleKey={toggleKey}
                form={form}
                similarItems={similarItems} />)}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                onClick={onClosePopup}
                className="w-full rounded-lg border-2 border-gray-400 py-2 text-gray-400"
              >
                CANCEL
              </button>

              <button onClick={gotoStep2} className="rounded-lg bg-red-600 px-8 py-2 font-bold text-white hover:bg-red-800">
                {form.step === 0 ? "SUBSCRIBE" : "SEE MORE ITEMS"}
              </button>
            </div>
          </FormWrapper>
        </div>
      </div>
    </div>
  )
}
FavoriteItems.propTypes = {
  product: PropTypes.object,
  setFavorite: PropTypes.func,
}

export default FavoriteItems
